import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Calendar, ChevronLeft, ChevronRight, Plus, Trash2, Printer,
  Share2, Loader2, TrendingUp, Wallet, X, Building2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

// Single reusable board for both:
//  • Daily profits (resource=profits, granularity=day)   → /manager/profits/{YYYY-MM-DD}
//  • Monthly expenses (resource=expenses, granularity=month) → /manager/expenses/{YYYY-MM}
//
// Behaviour is identical per row; the difference is the picker (calendar
// of days vs. grid of 12 months) and the color theme.

const MONTHS_AR = ["كانون الثاني","شباط","آذار","نيسان","أيار","حزيران","تموز","آب","أيلول","تشرين الأول","تشرين الثاني","كانون الأول"];
const pad2 = (n) => String(n).padStart(2, "0");

const ManagerLedger = ({ resource = "profits" }) => {
  const isProfits = resource === "profits";
  const theme = isProfits
    ? { primary: "#1E3A5F", accent: "#E85D45", title: "أرباح الشركة", emptyHint: "اختر اليوم لإضافة أرباح", icon: TrendingUp }
    : { primary: "#E85D45", accent: "#1E3A5F", title: "مصاريف الشركة", emptyHint: "اختر الشهر لإضافة مصاريف", icon: Wallet };
  const Icon = theme.icon;

  // Current navigation cursors
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1-12
  const [selectedKey, setSelectedKey] = useState(null); // day "YYYY-MM-DD" or month "YYYY-MM"
  const [keyToDoc, setKeyToDoc] = useState({});         // {key: doc}
  const [loading, setLoading] = useState(false);

  // Inline-row form
  const [formName, setFormName] = useState("");
  const [formAmount, setFormAmount] = useState("");
  const [formNote, setFormNote] = useState("");

  // Load list of existing docs (with rows) for the current cursor view.
  const loadList = async () => {
    setLoading(true);
    try {
      const url = isProfits
        ? `${API}/manager/profits?month=${year}-${pad2(month)}`
        : `${API}/manager/expenses?year=${year}`;
      const { data } = await axios.get(url);
      const map = {};
      (isProfits ? data.days : data.months).forEach((d) => {
        map[isProfits ? d.day : d.month] = d;
      });
      setKeyToDoc(map);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل تحميل البيانات");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadList(); }, [year, month, resource]);

  // ─── Picker grids ───
  const daysInMonth = useMemo(() => {
    if (!isProfits) return [];
    const last = new Date(year, month, 0).getDate(); // 28-31
    return Array.from({ length: last }, (_, i) => i + 1);
  }, [isProfits, year, month]);

  const openKey = async (key) => {
    setSelectedKey(key);
    // Lazy-load full doc (rows) if not yet cached
    if (!keyToDoc[key] || !keyToDoc[key].rows) {
      try {
        const url = isProfits
          ? `${API}/manager/profits/${key}`
          : `${API}/manager/expenses/${key}`;
        const { data } = await axios.get(url);
        const doc = isProfits ? data.day : data.month;
        setKeyToDoc((m) => ({ ...m, [key]: doc }));
      } catch (e) {
        toast.error(e?.response?.data?.detail || "فشل التحميل");
      }
    }
  };

  const active = selectedKey ? keyToDoc[selectedKey] : null;
  const total = (active?.rows || []).reduce((s, r) => s + Number(r.amount || 0), 0);

  const addRow = async () => {
    if (!formName.trim()) { toast.error("اكتب اسم النقطة/الفرع"); return; }
    try {
      const base = isProfits ? "profits" : "expenses";
      const { data } = await axios.post(`${API}/manager/${base}/${selectedKey}/rows`, {
        name: formName, amount: Number(formAmount || 0), note: formNote,
      });
      setKeyToDoc((m) => ({
        ...m,
        [selectedKey]: { ...(m[selectedKey] || { rows: [] }), rows: [...((m[selectedKey] || {}).rows || []), data.row] },
      }));
      setFormName(""); setFormAmount(""); setFormNote("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الإضافة");
    }
  };

  const updateRow = async (row, patch) => {
    try {
      const base = isProfits ? "profits" : "expenses";
      await axios.patch(`${API}/manager/${base}/${selectedKey}/rows/${row.id}`, patch);
      setKeyToDoc((m) => ({
        ...m,
        [selectedKey]: {
          ...m[selectedKey],
          rows: m[selectedKey].rows.map((r) => r.id === row.id ? { ...r, ...patch } : r),
        },
      }));
    } catch { toast.error("فشل التحديث"); }
  };

  const deleteRow = async (row) => {
    if (!window.confirm(`حذف «${row.name}»؟`)) return;
    try {
      const base = isProfits ? "profits" : "expenses";
      await axios.delete(`${API}/manager/${base}/${selectedKey}/rows/${row.id}`);
      setKeyToDoc((m) => ({
        ...m,
        [selectedKey]: { ...m[selectedKey], rows: m[selectedKey].rows.filter((r) => r.id !== row.id) },
      }));
    } catch { toast.error("فشل الحذف"); }
  };

  const labelOfKey = (key) =>
    isProfits ? key : `${MONTHS_AR[parseInt(key.split("-")[1], 10) - 1]} ${key.split("-")[0]}`;

  const buildShareText = () => {
    if (!active) return "";
    const lines = [];
    lines.push(`📊 ${theme.title} — ${labelOfKey(selectedKey)}`);
    lines.push("─".repeat(20));
    (active.rows || []).forEach((r, i) => {
      lines.push(`${i + 1}. ${r.name}: ${fmt(r.amount)} د.ع` + (r.note ? `\n   📝 ${r.note}` : ""));
    });
    lines.push("─".repeat(20));
    lines.push(`المجموع: ${fmt(total)} د.ع`);
    return lines.join("\n");
  };

  const shareWhatsApp = () => {
    if (!active || (active.rows || []).length === 0) { toast.error("لا يوجد سطور للمشاركة"); return; }
    window.open(`https://wa.me/?text=${encodeURIComponent(buildShareText())}`, "_blank");
  };

  const printBoard = () => {
    if (!active || (active.rows || []).length === 0) { toast.error("لا يوجد سطور للطباعة"); return; }
    const w = window.open("", "_blank", "width=820,height=900");
    if (!w) return;
    const rows = (active.rows || []).map((r, i) =>
      `<tr><td dir="ltr">${i + 1}</td><td>${r.name}</td><td class="num" dir="ltr">${fmt(r.amount)} د.ع</td><td>${r.note || "—"}</td></tr>`
    ).join("");
    w.document.write(`<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"><title>${theme.title} — ${labelOfKey(selectedKey)}</title>
<style>
  body { font-family: 'Tajawal', system-ui, sans-serif; padding: 20px; color: #111; direction: rtl; }
  h1 { color: ${theme.primary}; margin: 0 0 10px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #E4EBE6; padding: 8px; text-align: right; font-size: 13px; }
  th { background: ${theme.primary}; color: white; }
  .num { font-weight: 800; color: ${theme.accent}; }
  .total { margin-top: 14px; padding: 12px; background: #F0F9FF; border: 2px solid ${theme.primary}; border-radius: 12px; font-size: 16px; }
</style></head><body>
  <h1>📊 ${theme.title} — ${labelOfKey(selectedKey)}</h1>
  <table>
    <thead><tr><th>#</th><th>الاسم</th><th>المبلغ</th><th>ملاحظة</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="total"><b>المجموع الكلي:</b> <span dir="ltr">${fmt(total)} د.ع</span></div>
</body></html>`);
    w.document.close(); w.focus();
    setTimeout(() => w.print(), 400);
  };

  // ─── Render ───
  if (selectedKey) {
    // Active document editor
    return (
      <div className="space-y-3" dir="rtl" data-testid={`manager-${resource}-editor`}>
        <div className="rounded-2xl p-3 flex items-center gap-2 text-white" style={{ background: `linear-gradient(to left, ${theme.primary}, ${theme.accent})` }}>
          <Icon className="w-5 h-5" />
          <div className="flex-1 min-w-0">
            <p className="font-bold truncate">{theme.title} — {labelOfKey(selectedKey)}</p>
            <p className="text-[10px] opacity-90">{(active?.rows || []).length} نقطة · <span dir="ltr">{fmt(total)} د.ع</span></p>
          </div>
          <button onClick={() => setSelectedKey(null)} data-testid="ledger-back" className="bg-white/15 hover:bg-white/25 rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1">
            <X className="w-3 h-3" /> رجوع
          </button>
        </div>

        {/* Inline add */}
        <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6] grid grid-cols-1 sm:grid-cols-12 gap-2">
          <input value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="اسم النقطة/الفرع *" data-testid="ledger-name" className="sm:col-span-4 px-3 py-2 rounded-xl border-2 border-[#3B82F6] text-sm" />
          <input type="number" value={formAmount} onChange={(e) => setFormAmount(e.target.value)} placeholder="المبلغ" data-testid="ledger-amount" className="sm:col-span-3 px-3 py-2 rounded-xl border-2 border-[#E4EBE6] text-sm" />
          <input value={formNote} onChange={(e) => setFormNote(e.target.value)} placeholder="ملاحظة" data-testid="ledger-note" className="sm:col-span-4 px-3 py-2 rounded-xl border-2 border-[#E4EBE6] text-sm" />
          <button onClick={addRow} data-testid="ledger-add-row" className="sm:col-span-1 text-white font-bold rounded-xl flex items-center justify-center gap-1 py-2" style={{ background: theme.primary }}>
            <Plus className="w-4 h-4" />
          </button>
        </div>

        {/* Rows table */}
        <div className="bg-white rounded-2xl border-2 border-[#E4EBE6] overflow-x-auto">
          {(active?.rows || []).length === 0 ? (
            <p className="text-center py-10 text-sm text-[#5B6B62]">لا يوجد سطور — أضف من الأعلى.</p>
          ) : (
            <table className="w-full text-sm">
              <thead style={{ background: theme.primary }} className="text-white">
                <tr>
                  <th className="px-2 py-2 text-right w-12">#</th>
                  <th className="px-2 py-2 text-right">الاسم</th>
                  <th className="px-2 py-2 text-right w-36">المبلغ</th>
                  <th className="px-2 py-2 text-right">ملاحظة</th>
                  <th className="px-2 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {(active?.rows || []).map((r, i) => (
                  <tr key={r.id} className="border-t border-[#E4EBE6] hover:bg-[#F8FAFC]">
                    <td dir="ltr" className="px-2 py-2 text-center text-xs text-[#5B6B62]">{i + 1}</td>
                    <td className="px-2 py-2">
                      <input defaultValue={r.name} onBlur={(e) => e.target.value !== r.name && updateRow(r, { name: e.target.value })} className="bg-transparent w-full focus:bg-white focus:border-[#3B82F6] border border-transparent rounded px-1 py-0.5 text-sm font-bold" />
                    </td>
                    <td className="px-2 py-2">
                      <input type="number" defaultValue={r.amount} onBlur={(e) => Number(e.target.value) !== Number(r.amount) && updateRow(r, { amount: Number(e.target.value) })} className="bg-transparent w-32 focus:bg-white focus:border-[#3B82F6] border border-transparent rounded px-1 py-0.5 text-sm font-bold" style={{ color: theme.accent }} dir="ltr" />
                    </td>
                    <td className="px-2 py-2">
                      <input defaultValue={r.note || ""} onBlur={(e) => e.target.value !== r.note && updateRow(r, { note: e.target.value })} placeholder="—" className="bg-transparent w-full focus:bg-white focus:border-[#3B82F6] border border-transparent rounded px-1 py-0.5 text-xs" />
                    </td>
                    <td className="px-2 py-2">
                      <button onClick={() => deleteRow(r)} className="text-[#E85D45] hover:bg-[#FEE2E2] rounded-full p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Total + actions */}
        <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6] flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 ms-auto">
            <span className="text-xs text-[#5B6B62]">المجموع الكلي:</span>
            <span className="text-xl font-extrabold" style={{ color: theme.primary }} dir="ltr">{fmt(total)} د.ع</span>
          </div>
          <button onClick={printBoard} data-testid="ledger-print" className="bg-[#1E3A5F] hover:bg-[#152a45] text-white text-xs font-bold py-2 px-3 rounded-full flex items-center gap-1">
            <Printer className="w-3.5 h-3.5" /> طباعة
          </button>
          <button onClick={shareWhatsApp} data-testid="ledger-whatsapp" className="bg-[#25D366] hover:bg-[#1ebe57] text-white text-xs font-bold py-2 px-3 rounded-full flex items-center gap-1">
            <Share2 className="w-3.5 h-3.5" /> واتساب
          </button>
        </div>
      </div>
    );
  }

  // ─── Picker (no key selected) ───
  return (
    <div className="space-y-3" dir="rtl" data-testid={`manager-${resource}-picker`}>
      <div className="rounded-2xl p-4 text-white flex items-center gap-3" style={{ background: `linear-gradient(to left, ${theme.primary}, ${theme.accent})` }}>
        <Icon className="w-6 h-6" />
        <div className="flex-1">
          <h3 className="font-bold text-lg">{theme.title}</h3>
          <p className="text-xs opacity-90">سجلّ خاص بك للملاحظات — مستقلّ تماماً عن الحسابات الرئيسية.</p>
        </div>
      </div>

      {/* Year/Month navigator */}
      <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6] flex items-center justify-between gap-2">
        <button
          onClick={() => isProfits
            ? (month === 1 ? (setYear(year - 1), setMonth(12)) : setMonth(month - 1))
            : setYear(year - 1)}
          className="bg-[#F0F9FF] hover:bg-[#E0F2FE] rounded-full p-2"
          data-testid="ledger-prev"
        >
          <ChevronRight className="w-5 h-5 text-[#1E3A5F]" />
        </button>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[#5B6B62]" />
          <p className="font-bold text-[#111814]">
            {isProfits ? `${MONTHS_AR[month - 1]} ${year}` : `سنة ${year}`}
          </p>
        </div>
        <button
          onClick={() => isProfits
            ? (month === 12 ? (setYear(year + 1), setMonth(1)) : setMonth(month + 1))
            : setYear(year + 1)}
          className="bg-[#F0F9FF] hover:bg-[#E0F2FE] rounded-full p-2"
          data-testid="ledger-next"
        >
          <ChevronLeft className="w-5 h-5 text-[#1E3A5F]" />
        </button>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#1E3A5F] inline" /></div>
      ) : (
        <div className={`grid gap-2 ${isProfits ? "grid-cols-4 sm:grid-cols-7" : "grid-cols-3 sm:grid-cols-4"}`}>
          {(isProfits ? daysInMonth : [1,2,3,4,5,6,7,8,9,10,11,12]).map((n) => {
            const key = isProfits ? `${year}-${pad2(month)}-${pad2(n)}` : `${year}-${pad2(n)}`;
            const doc = keyToDoc[key];
            const sum = (doc?.rows || []).reduce((s, r) => s + Number(r.amount || 0), 0);
            const filled = !!(doc && (doc.rows || []).length > 0);
            return (
              <button
                key={key}
                onClick={() => openKey(key)}
                data-testid={`pick-${resource}-${key}`}
                className={`relative rounded-2xl p-3 border-2 transition-all hover:scale-105 hover:shadow-lg text-right ${
                  filled
                    ? "text-white shadow-md"
                    : "bg-white border-[#E4EBE6] hover:border-[#3B82F6]"
                }`}
                style={filled ? { background: `linear-gradient(135deg, ${theme.primary}, ${theme.accent})`, borderColor: theme.primary } : {}}
              >
                <p className={`text-2xl font-extrabold ${filled ? "" : "text-[#1E3A5F]"}`} dir="ltr">
                  {isProfits ? n : MONTHS_AR[n - 1].slice(0, 4)}
                </p>
                <p className={`text-[10px] mt-1 ${filled ? "opacity-90" : "text-[#5B6B62]"}`}>
                  {filled ? `${(doc.rows || []).length} نقطة` : theme.emptyHint}
                </p>
                {filled && (
                  <p className="text-[11px] font-bold mt-1" dir="ltr">{fmt(sum)} د.ع</p>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ManagerLedger;
